import React, { useEffect, useState } from 'react';
import useAuth from '../../pages/useAuth';
import { Card, Row, Col, Form, Button, Table, Image, Modal, InputGroup } from 'react-bootstrap';

const ConsultantSignMaster = () => {
     useAuth();
     const baseUrl = process.env.REACT_APP_BASE_URL || '';
     const [signs, setSigns] = useState([]);
     const [loading, setLoading] = useState(false);
     const [saving, setSaving] = useState(false);
     const [message, setMessage] = useState(null);

     const [form, setForm] = useState({ emp_code: '', file: null });
     const [preview, setPreview] = useState(null);
     const [showPreview, setShowPreview] = useState(false);
     const [showFormModal, setShowFormModal] = useState(false);
     const [filterEmp, setFilterEmp] = useState('');
     const [page, setPage] = useState(1);
     const [pageSize, setPageSize] = useState(10);

     const listUrl = baseUrl ? `${baseUrl}/consultantSigns` : '/consultantSigns';
     const createUrl = baseUrl ? `${baseUrl}/consultantSign` : '/consultantSign';

     useEffect(() => {
          fetchList();
     }, []);

     async function fetchList() {
          setLoading(true);
          try {
               const token = localStorage.getItem('token');
               const headers = { Accept: 'application/json' };
               if (token) headers.Authorization = `Bearer ${token}`;
               const res = await fetch(listUrl, { method: 'GET', headers });
               const json = await res.json();
               if (Array.isArray(json)) setSigns(json);
               else if (json && Array.isArray(json.data)) setSigns(json.data);
               else setSigns([]);
          } catch (err) {
               console.error(err);
               setMessage({ type: 'error', text: 'Failed to load sign list' });
          } finally {
               setLoading(false);
          }
     }

     function handleFileChange(e) {
          const f = e.target.files && e.target.files[0];
          if (!f) {
               setForm(f => ({ ...f, file: null }));
               setPreview(null);
               return;
          }
          const allowed = ['image/jpeg', 'image/png', 'image/jpg'];
          if (!allowed.includes(f.type)) {
               setMessage({ type: 'error', text: 'Only JPG, JPEG or PNG files are allowed' });
               e.target.value = '';
               return;
          }
          // limit to 5MB
          const maxSize = 5 * 1024 * 1024;
          if (f.size > maxSize) {
               setMessage({ type: 'error', text: 'File too large (max 5 MB)' });
               e.target.value = '';
               return;
          }
          setForm(fm => ({ ...fm, file: f }));
          const url = URL.createObjectURL(f);
          setPreview(url);
     }

     function handleFieldChange(e) {
          const { name, value } = e.target;
          setForm(f => ({ ...f, [name]: value }));
     }

     async function handleSubmit(e) {
          e && e.preventDefault();
          setMessage(null);
          if (!form.emp_code || !form.file) {
               setMessage({ type: 'error', text: 'Emp code and image file are required' });
               return;
          }
          setSaving(true);
          try {
               const fd = new FormData();
               fd.append('emp_code', form.emp_code);
               fd.append('file', form.file);
               const token = localStorage.getItem('token');
               const headers = {};
               if (token) headers.Authorization = `Bearer ${token}`;
               const res = await fetch(createUrl, { method: 'POST', headers, body: fd });
               const json = await res.json();
               if (res.ok) {
                    setMessage({ type: 'success', text: 'Uploaded' });
                    setForm({ emp_code: '', file: null });
                    setPreview(null);
                    fetchList();
               } else {
                    setMessage({ type: 'error', text: json.error || JSON.stringify(json) });
               }
          } catch (err) {
               console.error(err);
               setMessage({ type: 'error', text: 'Upload failed' });
          } finally {
               setSaving(false);
          }
     }

     async function handleDelete(id) {
          if (!window.confirm('Delete this signature?')) return;
          try {
               const token = localStorage.getItem('token');
               const headers = {};
               if (token) headers.Authorization = `Bearer ${token}`;
               const url = baseUrl ? `${baseUrl}/consultantSign/${id}` : `/consultantSign/${id}`;
               const res = await fetch(url, { method: 'DELETE', headers });
               if (res.ok) {
                    setMessage({ type: 'success', text: 'Deleted' });
                    fetchList();
               } else {
                    const json = await res.json();
                    setMessage({ type: 'error', text: json.error || 'Delete failed' });
               }
          } catch (err) {
               console.error(err);
               setMessage({ type: 'error', text: 'Delete failed' });
          }
     }

     // derived list: filter + pagination
     const filtered = signs.filter(s => {
          if (!filterEmp) return true;
          return String(s.emp_code || '').includes(String(filterEmp));
     });

     const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
     const pageSafe = Math.min(page, totalPages);
     const paged = filtered.slice((pageSafe - 1) * pageSize, pageSafe * pageSize);

     function openPreview(url) {
          setPreview(url);
          setShowPreview(true);
     }

     function formatDateTime(val) {
          if (!val && val !== 0) return '';
          try {
               // handle numeric epochs (seconds or milliseconds)
               if (typeof val === 'number' || /^\d+$/.test(String(val))) {
                    const n = Number(val);
                    const date = n > 1e12 ? new Date(n) : new Date(n * 1000);
                    if (!isNaN(date.getTime())) return date.toLocaleString();
               }
               const d = new Date(val);
               if (!isNaN(d.getTime())) return d.toLocaleString();
          } catch (e) {
               // fallthrough
          }
          return String(val);
     }

     return (
          <div style={{ padding: 20 }}>
               {/* Use default Bootstrap modal styling for a native look */}
               <Card>
                    <Card.Body>
                         <div className="row mb-3">
                              <div className="col">
                                   <h4>Consultant Signatures</h4>
                              </div>
                              <div className="col">

                                   <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                        <div>
                                             <button className="icon-btn" aria-label="Open upload form" title="Upload signature" size="sm" variant="primary" onClick={() => setShowFormModal(true)} style={{ padding: '6px 8px' }}>➕</button>
                                        </div>

                                   </div>
                              </div>
                         </div>

                         <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <InputGroup style={{ maxWidth: 280 }}>
                                   <InputGroup.Text id="filter-emp" style={{ fontSize: 12 }}>Filter Emp</InputGroup.Text>
                                   <Form.Control size="sm" placeholder="Emp code" aria-label="Emp code" aria-describedby="filter-emp" value={filterEmp} onChange={e => { setFilterEmp(e.target.value); setPage(1); }} />
                              </InputGroup>
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                   <div style={{ fontSize: 13 }}>Rows:</div>
                                   <Form.Select size="sm" value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }} style={{ width: 80 }}>
                                        <option value={5}>5</option>
                                        <option value={10}>10</option>
                                        <option value={20}>20</option>
                                   </Form.Select>
                              </div>
                         </div>

                         <div>
                              <Table striped hover size="sm" className="table-compact">
                                   <thead>
                                        <tr>
                                             <th style={{ width: 36 }}>#</th>
                                             <th>Emp Code</th>
                                             <th style={{ width: 110 }}>Preview</th>
                                             <th style={{ width: 220 }}>Created</th>
                                             <th style={{ width: 64 }}>Action</th>
                                        </tr>
                                   </thead>
                                   <tbody>
                                        {loading && (
                                             <tr><td colSpan={5} className="text-center">Loading…</td></tr>
                                        )}
                                        {!loading && filtered.length === 0 && (
                                             <tr><td colSpan={5} className="text-center">No records</td></tr>
                                        )}
                                        {!loading && paged.map((s, i) => (
                                             <tr key={s.id || i}>
                                                  <td>{(pageSafe - 1) * pageSize + i + 1}</td>
                                                  <td>{s.emp_code}</td>
                                                  <td style={{ width: 110, cursor: s.file_url ? 'pointer' : 'default' }} onClick={() => s.file_url && openPreview(s.file_url)}>
                                                       {s.file_url ? <Image src={s.file_url} thumbnail style={{ maxHeight: 50 }} /> : '-'}
                                                  </td>
                                                  <td style={{ width: 220, whiteSpace: 'nowrap' }} title={s.created_dtm || s.createdAt || ''}>{formatDateTime(s.created_dtm || s.createdAt)}</td>
                                                  <td>
                                                       <Button size="sm" variant="outline-danger" onClick={() => handleDelete(s.id)} title="Delete" style={{ padding: '4px 6px' }}>🗑️</Button>
                                                  </td>
                                             </tr>
                                        ))}
                                   </tbody>
                              </Table>

                              {/* Form modal */}
                              <Modal show={showFormModal} onHide={() => { setShowFormModal(false); setPreview(null); }} centered>
                                   <form className="modal-form" onSubmit={e => { handleSubmit(e); setShowFormModal(false); }}>

                                        <Modal.Body className="m-4">
                                             <Row className="align-items-end">
                                                  <Col md={12} className="mb-2">
                                                       <Form.Group>
                                                            <Form.Label>Emp Code</Form.Label>
                                                            <Form.Control size="sm" name="emp_code" value={form.emp_code} onChange={handleFieldChange} placeholder="Enter emp code" />
                                                       </Form.Group>
                                                  </Col>
                                                  <Col md={12} className="mb-2">
                                                       <Form.Group>
                                                            <Form.Label>Image (JPG / PNG, max 5MB)</Form.Label>
                                                            <Form.Control size="sm" type="file" accept="image/jpeg,image/png" onChange={handleFileChange} />
                                                       </Form.Group>
                                                  </Col>
                                                  {preview && (
                                                       <Col md={12} className="text-center mt-2">
                                                            <Image src={preview} thumbnail style={{ maxHeight: 80 }} />
                                                       </Col>
                                                  )}
                                             </Row>
                                        </Modal.Body>
                                        <Modal.Footer>
                                             <Button type="submit" variant="primary" disabled={saving}>{saving ? 'Uploading…' : 'Upload'}</Button>
                                             <Button variant="secondary" onClick={() => { setForm({ emp_code: '', file: null }); setPreview(null); }}>Reset</Button>
                                        </Modal.Footer>
                                   </form>
                              </Modal>

                              {/* pagination */}
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                                   <div style={{ fontSize: 13 }}>Showing {filtered.length === 0 ? 0 : (pageSafe - 1) * pageSize + 1} - {Math.min(filtered.length, pageSafe * pageSize)} of {filtered.length}</div>
                                   <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                        <Button size="sm" variant="light" disabled={pageSafe <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Prev</Button>
                                        <div style={{ minWidth: 40, textAlign: 'center' }}>{pageSafe} / {totalPages}</div>
                                        <Button size="sm" variant="light" disabled={pageSafe >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next</Button>
                                   </div>
                              </div>
                         </div>
                         {/* image preview modal */}
                         <Modal show={showPreview} onHide={() => setShowPreview(false)} size="lg" centered>
                              <Modal.Header closeButton>
                                   <Modal.Title>Preview</Modal.Title>
                              </Modal.Header>
                              <Modal.Body className="text-center">
                                   {preview ? <Image src={preview} fluid style={{ maxHeight: '70vh' }} /> : <div>No image</div>}
                              </Modal.Body>
                         </Modal>
                    </Card.Body>
               </Card>
          </div>
     );
};

export default ConsultantSignMaster;
